"use client";

import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import { Network } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CsLoadingScreen } from "@/components/ui/cs-loading-screen";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Seat, SeatingPlanBlock, SeatingPlanTemplate } from "@/lib/types";
import { ClanTagBadge } from "@/components/ui/clan-tag-badge";
import { cn } from "@/lib/utils";
import { PlanElementBox } from "@/components/section-seating/plan-element-box";
import { reserveSeat, cancelReservation } from "@/query/seats";
import { toast } from "sonner";

interface SeatingPlanProps {
  seats: Seat[];
  template: SeatingPlanTemplate | null;
  currentUserId: string;
  currentUserName: string;
  isLoggedIn?: boolean;
  isRegistrationOpen: boolean;
  eventId: number;
  softReserveLimit?: number;
  readOnly?: boolean;
  isRegistered?: boolean;
  hasPaid?: boolean;
  isAdmin?: boolean;
}

export default function SeatingPlan({
  seats,
  template,
  currentUserId,
  currentUserName,
  isLoggedIn = true,
  isRegistrationOpen,
  eventId,
  softReserveLimit = 7,
  readOnly = false,
  isRegistered = false,
  hasPaid = false,
  isAdmin = false,
}: SeatingPlanProps) {
  const { resolvedTheme } = useTheme();
  const [seatData, setSeatData] = useState<Seat[]>(seats);
  const [selectedSeat, setSelectedSeat] = useState<Seat | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fakeLoading, setFakeLoading] = useState(false);

  useEffect(() => {
    if (resolvedTheme !== "cs16") return;
    setFakeLoading(true);
    const timer = setTimeout(() => setFakeLoading(false), 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSeatData(seats);
  }, [seats]);

  // Available width for the floor plan, used to scale it down to fit
  const planContainerRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  useEffect(() => {
    const el = planContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setAvailableWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const userHardCount = seatData.filter((s) => s.reserved_by === currentUserId && s.reservation_type === "hard").length;
  const userSoftCount = seatData.filter((s) => s.reserved_by === currentUserId && s.reservation_type === "soft").length;

  const seatGrid = new Map<string, Seat>();
  seatData.forEach((s) => seatGrid.set(`${s.seat_row}-${s.seat_column}`, s));

  const totalSeats = seatData.length;
  const hardReservedCount = seatData.filter((s) => s.is_reserved && s.reservation_type === "hard").length;
  const softReservedCount = seatData.filter((s) => s.is_reserved && s.reservation_type === "soft").length;
  // Vorgemerkte (soft) Plätze gelten als frei — sie können jederzeit fest reserviert werden.
  const availableCount = totalSeats - hardReservedCount;

  const handleSeatClick = (seat: Seat) => {
    if (!isRegistrationOpen || readOnly) return;
    if (seat.is_admin_only && !isAdmin) return;
    setSelectedSeat(seat);
  };

  const handleReserveSeat = async (seat: Seat, type: "hard" | "soft") => {
    setIsLoading(true);
    try {
      const result = await reserveSeat(seat.seat_id, currentUserId, eventId, type);
      if (result.success) {
        setSeatData((prev) =>
          prev.map((s) => {
            if (s.seat_id !== seat.seat_id) return s;
            return {
              ...s,
              is_reserved: true,
              reserved_by: currentUserId,
              reserved_by_name: currentUserName,
              reservation_time: new Date(),
              reservation_type: type,
            };
          }),
        );
        toast.success(type === "hard" ? `Platz ${getSeatLabel(seat)} fest reserviert!` : `Platz ${getSeatLabel(seat)} vorgemerkt!`);
        setSelectedSeat(null);
      } else {
        const messages: Record<string, string> = {
          paid: "Dieser Platz wurde bereits bezahlt.",
          hard_reserved: "Dieser Platz ist bereits fest reserviert.",
          already_hard_reserved: "Du hast bereits einen Platz fest reserviert.",
          not_paid: "Du kannst erst einen Platz fest reservieren, wenn du bezahlt hast.",
          not_registered: "Du musst dich zuerst für das Event anmelden, um einen Platz vorzumerken.",
          soft_limit_reached: `Du kannst maximal ${softReserveLimit} Plätze vormerken.`,
          already_reserved: "Dieser Platz ist bereits reserviert.",
          admin_only: "Dieser Platz ist nur für Admins reservierbar.",
        };
        toast.error(messages[result.reason ?? ""] ?? "Fehler beim Reservieren.");
        window.location.reload();
      }
    } catch {
      toast.error("Fehler beim Reservieren des Platzes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelReservation = async (seat: Seat) => {
    setIsLoading(true);
    try {
      const success = await cancelReservation(seat.seat_id, currentUserId, seat.reservation_type ?? "hard");
      if (success) {
        setSeatData((prev) =>
          prev.map((s) =>
            s.seat_id === seat.seat_id
              ? {
                  ...s,
                  is_reserved: false,
                  reserved_by: null,
                  reserved_by_name: null,
                  reservation_time: null,
                  reservation_type: null,
                }
              : s,
          ),
        );
        toast.success(`Reservierung für Platz ${getSeatLabel(seat)} aufgehoben!`);
        setSelectedSeat(null);
      } else {
        toast.error("Fehler beim Aufheben der Reservierung.");
      }
    } catch {
      toast.error("Fehler beim Aufheben der Reservierung.");
    } finally {
      setIsLoading(false);
    }
  };

  const getSeatColor = (seat: Seat) => {
    if (seat.is_reserved && seat.reserved_by === currentUserId && seat.reservation_type === "hard") return "bg-green-500 hover:bg-amber-400 border-amber-400 cursor-pointer";
    if (seat.is_reserved && seat.reservation_type === "hard") return "bg-green-500 border-green-600 cursor-not-allowed";
    if (seat.is_reserved && seat.reservation_type === "soft") return "bg-amber-300 border-amber-400 cursor-pointer";
    if (seat.is_admin_only) {
      return isAdmin && isRegistrationOpen && !readOnly ? "bg-purple-400 hover:bg-purple-500 border-purple-600 cursor-pointer" : "bg-purple-300 dark:bg-purple-800 border-purple-500 dark:border-purple-600 cursor-not-allowed";
    }
    if (!isRegistrationOpen || readOnly) return "bg-[#9ca3af] dark:bg-[#6b7280] border-[#6b7280] dark:border-[#9ca3af] cursor-not-allowed";
    return "bg-[#9ca3af] dark:bg-[#6b7280] hover:bg-[#6b7280] dark:hover:bg-[#9ca3af] border-[#6b7280] dark:border-[#9ca3af] cursor-pointer";
  };

  const getSeatTextColor = (seat: Seat) => {
    if (seat.is_reserved && seat.reservation_type === "soft") return "text-amber-900";
    if (seat.is_reserved) return "text-white";
    if (seat.is_admin_only) return "text-white";
    return "text-gray-800 dark:text-gray-100";
  };

  const getSeatStateClass = (seat: Seat) => {
    if (seat.is_reserved && seat.reservation_type === "hard") return "seat-hard";
    if (seat.is_reserved && seat.reserved_by === currentUserId) return "seat-own-soft";
    if (seat.is_reserved && seat.reservation_type === "soft") return "seat-soft";
    if (seat.is_admin_only) return "seat-admin-only";
    return "seat-available";
  };

  // ── No template ────────────────────────────────────────────────────────

  if (!template || !template.blocks?.length) {
    return <div className="py-12 text-center text-muted-foreground">Kein Sitzplan-Layout zugewiesen.</div>;
  }

  // ── Build per-block seat lookup & label map ────────────────────────────

  const blocks = template.blocks;

  const seatsByBlock = new Map<number, Seat[]>();
  seatData.forEach((s) => {
    if (s.block_id == null) return;
    if (!seatsByBlock.has(s.block_id)) seatsByBlock.set(s.block_id, []);
    seatsByBlock.get(s.block_id)!.push(s);
  });

  // The label (blockLetter + sequential number) is stored in seat_number
  // since migration 031 — no client-side derivation needed anymore.
  const getSeatLabel = (seat: Seat) => seat.seat_number;

  // ── Canvas geometry ────────────────────────────────────────────────────

  // Display cell size: 40px seat + 4px spacing
  const CELL = 44;
  const elements = template.elements ?? [];

  const footprintOf = (b: SeatingPlanBlock) => {
    const len = b.seats + (b.switch_after != null ? 1 : 0);
    return b.orientation === "horizontal" ? { w: b.lanes, h: len } : { w: len, h: b.lanes };
  };

  const planW = Math.max(1, ...blocks.map((b) => b.pos_x + footprintOf(b).w), ...elements.map((e) => e.pos_x + e.width));
  const planH = Math.max(1, ...blocks.map((b) => b.pos_y + footprintOf(b).h), ...elements.map((e) => e.pos_y + e.height));

  // Canvas cell of a seat, for anchoring the reservation popover.
  // Seats after a switch render one cell further along than their coordinate.
  const seatAnchorPos = (seat: Seat): { x: number; y: number } | null => {
    const block = blocks.find((b) => b.id === seat.block_id);
    if (!block) return null;
    if (block.orientation === "horizontal") {
      const r = seat.seat_row - block.pos_y - 1;
      const shift = block.switch_after != null && r >= block.switch_after ? 1 : 0;
      return { x: seat.seat_column - 1, y: block.pos_y + r + shift };
    }
    const c = seat.seat_column - block.pos_x - 1;
    const shift = block.switch_after != null && c >= block.switch_after ? 1 : 0;
    return { x: block.pos_x + c + shift, y: seat.seat_row - 1 };
  };
  const anchorPos = selectedSeat ? seatAnchorPos(selectedSeat) : null;

  // Scale the plan down to fit the available width (p-6 padding + border = 52px),
  // but no smaller than 45% so seats stay recognizable — beyond that we scroll.
  const unscaledW = planW * CELL + 52;
  const unscaledH = planH * CELL + 52;
  const scale = availableWidth > 0 ? Math.max(0.45, Math.min(1, availableWidth / unscaledW)) : 1;

  const userHardSeats = seatData.filter((s) => s.reserved_by === currentUserId && s.reservation_type === "hard");
  const userSoftSeats = seatData.filter((s) => s.reserved_by === currentUserId && s.reservation_type === "soft");

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-4">
      {fakeLoading && <CsLoadingScreen text="Connecting to server..." />}
      {!isRegistrationOpen && !readOnly && <p className="text-center text-sm text-muted-foreground">Die Sitzplatzreservierung ist geschlossen.</p>}

      {/* Venue floor plan */}
      <Popover
        open={!!selectedSeat}
        onOpenChange={(open) => {
          if (!open) setSelectedSeat(null);
        }}
        modal={false}
      >
        {/* User's own reservations summary */}
        {!readOnly && (userHardCount > 0 || userSoftCount > 0) && (
          <div className="p-3 bg-muted/50 border rounded-lg text-sm flex items-center justify-between w-full gap-4">
            {userHardCount > 0 && (
              <div className="flex items-center gap-2">
                <span>
                  Deine Reservierung: <strong>{userHardSeats.map(getSeatLabel).join(", ")}</strong>
                </span>
              </div>
            )}
            {userSoftCount > 0 && (
              <span>
                Vorgemerkt ({userSoftCount}/{softReserveLimit}):{" "}
                <strong>
                  {seatData
                    .filter((s) => s.reserved_by === currentUserId && s.reservation_type === "soft")
                    .map(getSeatLabel)
                    .join(", ")}
                </strong>
              </span>
            )}{" "}
            {(userHardCount > 0 || userSoftCount > 0) && (
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    userHardSeats.forEach((seat) => handleCancelReservation(seat));
                    userSoftSeats.forEach((seat) => handleCancelReservation(seat));
                  }}
                >
                  Alle Reservierungen und Vormerkungen aufheben
                </Button>
              </div>
            )}
          </div>
        )}
        {/* Legend + stats */}
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex gap-4 text-sm flex-wrap">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 bg-[#9ca3af] dark:bg-[#6b7280] rounded-sm border border-[#6b7280] dark:border-[#9ca3af]" />
              <span>Verfügbar</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 bg-amber-300 rounded-sm border border-amber-400" />
              <span>Vorgemerkt</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 bg-green-500 rounded-sm border border-green-600" />
              <span>Reserviert</span>
            </div>
            {seatData.some((s) => s.is_admin_only) && (
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 bg-purple-400 dark:bg-purple-700 rounded-sm border border-purple-600 dark:border-purple-500" />
                <span>Admin-Bereich</span>
              </div>
            )}
          </div>
          <div className="flex gap-4 text-sm text-muted-foreground ml-auto">
            <span>
              <strong className="text-foreground">{totalSeats}</strong> Gesamt
            </span>
            <span>
              <strong className="text-green-500">{hardReservedCount}</strong> Reserviert
            </span>
            <span>
              <strong className="text-amber-400">{softReservedCount}</strong> Vorgemerkt
            </span>
            <span>
              <strong className="text-foreground">{availableCount}</strong> Frei
            </span>
          </div>
        </div>
        <div ref={planContainerRef} className="overflow-x-auto">
          {/* transform: scale() doesn't affect layout, so this wrapper reserves the scaled footprint */}
          <div className="mx-auto" style={{ width: unscaledW * scale, height: unscaledH * scale }}>
            <div className="rounded-md bg-card p-6 origin-top-left" style={{ width: unscaledW, transform: `scale(${scale})` }}>
              <TooltipProvider>
                <div className="relative" style={{ width: planW * CELL, height: planH * CELL }}>
                  {/* Seat blocks */}
                  {blocks.map((block) => {
                    const blockSeats = seatsByBlock.get(block.id) ?? [];
                    const blockGrid = new Map<string, Seat>();
                    blockSeats.forEach((s) => blockGrid.set(`${s.seat_row}-${s.seat_column}`, s));

                    const rows = [...new Set(blockSeats.map((s) => s.seat_row))].sort((a, b) => a - b);
                    const cols = [...new Set(blockSeats.map((s) => s.seat_column))].sort((a, b) => a - b);

                    if (rows.length === 0 || cols.length === 0) return null;

                    const renderSeatCell = (seat: Seat | undefined, key: React.Key) => (
                      <div key={key} className="flex items-center justify-center shrink-0" style={{ width: CELL, height: CELL }}>
                        {seat && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => handleSeatClick(seat)}
                                disabled={
                                  readOnly ||
                                  !isRegistrationOpen ||
                                  (seat.is_admin_only && !isAdmin) ||
                                  (!!seat.has_paid && seat.reserved_by !== currentUserId) ||
                                  (seat.is_reserved && seat.reservation_type === "hard" && seat.reserved_by !== currentUserId)
                                }
                                data-reserved={seat.is_reserved ? "true" : "false"}
                                data-paid={seat.has_paid ? "true" : "false"}
                                data-own={seat.reserved_by === currentUserId ? "true" : "false"}
                                data-type={seat.reservation_type ?? "none"}
                                data-admin-only={seat.is_admin_only ? "true" : "false"}
                                className={cn(
                                  "seat-btn w-10 h-10 rounded-md border-2 transition-colors text-[10px] font-mono font-semibold",
                                  getSeatStateClass(seat),
                                  getSeatColor(seat),
                                  getSeatTextColor(seat),
                                  seat.is_reserved && seat.reserved_by === currentUserId && "ring-2 ring-offset-1 ring-orange-500",
                                  selectedSeat?.seat_id === seat.seat_id && "ring-2 ring-offset-1 ring-primary",
                                )}
                              >
                                {getSeatLabel(seat)}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-semibold text-sm">Platz {getSeatLabel(seat)}</p>
                              {!!seat.is_admin_only && <p className="text-xs text-purple-400">Admin-Bereich</p>}
                              {seat.is_reserved && seat.reserved_by_name ? (
                                <p className="text-xs flex items-center gap-1">
                                  {seat.reservation_type === "soft" ? "Vorgemerkt von" : "Reserviert von"}: {seat.reserved_by_name}
                                  <ClanTagBadge tag={seat.reserved_by_clan_tag} />
                                </p>
                              ) : (
                                <p className="text-xs text-green-400">Verfügbar</p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    );

                    const switchCell = (
                      <div className="flex items-center justify-center shrink-0" style={{ width: CELL, height: CELL }}>
                        <div className="w-10 h-10 flex flex-col items-center justify-center rounded-md border-2 border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-500 dark:text-blue-400">
                          <Network className="w-5 h-5" />
                          <span className="text-[8px] font-bold tracking-tight leading-none mt-0.5">SW</span>
                        </div>
                      </div>
                    );

                    // vertical: 2 rows ("lanes") × N cols — horizontal: 2 cols × N rows
                    const isHorizontal = block.orientation === "horizontal";
                    const lanes = isHorizontal ? cols : rows;
                    const cells = isHorizontal ? rows : cols;

                    const renderLane = (lane: number) => (
                      <div key={lane} className={isHorizontal ? "flex flex-col" : "flex"}>
                        {cells.map((cell, idx) => {
                          const gridKey = isHorizontal ? `${cell}-${lane}` : `${lane}-${cell}`;
                          return (
                            <React.Fragment key={cell}>
                              {block.switch_after != null && idx === block.switch_after && switchCell}
                              {renderSeatCell(blockGrid.get(gridKey), gridKey)}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    );

                    return (
                      <div key={block.id} className="absolute" style={{ left: block.pos_x * CELL, top: block.pos_y * CELL }}>
                        {block.label && <span className="absolute -top-4 inset-x-0 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest text-center">{block.label}</span>}
                        <div className={isHorizontal ? "flex" : "flex flex-col"}>{lanes.map(renderLane)}</div>
                      </div>
                    );
                  })}

                  {/* Info elements */}
                  {elements.map((el) => (
                    <div
                      key={el.id}
                      className="absolute"
                      style={{
                        left: el.pos_x * CELL,
                        top: el.pos_y * CELL,
                        width: el.width * CELL,
                        height: el.height * CELL,
                      }}
                    >
                      <PlanElementBox type={el.type} label={el.label} color={el.color} width={el.width} height={el.height} cell={CELL} />
                    </div>
                  ))}

                  {/* Invisible anchor for the reservation popover, at the selected seat */}
                  {anchorPos && (
                    <PopoverAnchor asChild>
                      <div className="absolute pointer-events-none" style={{ left: anchorPos.x * CELL, top: anchorPos.y * CELL, width: CELL, height: CELL }} />
                    </PopoverAnchor>
                  )}
                </div>
              </TooltipProvider>
            </div>
          </div>
        </div>

        {/* Reservation popover */}
        <PopoverContent className="w-80" onOpenAutoFocus={(e) => e.preventDefault()}>
          {selectedSeat && (
            <>
              <h3 className="font-semibold mb-3">Platz {getSeatLabel(selectedSeat)}</h3>
              {!isLoggedIn ? (
                <p className="text-sm text-muted-foreground">
                  <a href="/login" className="underline hover:text-foreground">
                    Melde dich an
                  </a>
                  , um einen Platz zu reservieren.
                </p>
              ) : (
                <>
                  {!!selectedSeat.is_reserved && !!selectedSeat.reserved_by_name && (
                    <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1">
                      {selectedSeat.reservation_type === "soft" ? "Vorgemerkt von" : "Reserviert von"}: {selectedSeat.reserved_by_name}
                      <ClanTagBadge tag={selectedSeat.reserved_by_clan_tag} />
                    </p>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    {selectedSeat.reserved_by === currentUserId ? (
                      <>
                        <Button onClick={() => handleCancelReservation(selectedSeat)} disabled={isLoading} variant="destructive" size="sm">
                          {isLoading ? "Wird aufgehoben…" : selectedSeat.reservation_type === "hard" ? "Reservierung aufheben" : "Vormerkung aufheben"}
                        </Button>
                        {selectedSeat.reservation_type === "soft" && hasPaid && userHardCount === 0 && (
                          <Button onClick={() => handleReserveSeat(selectedSeat, "hard")} disabled={isLoading} size="sm">
                            Fest reservieren
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        {hasPaid && (
                          <Button onClick={() => handleReserveSeat(selectedSeat, "hard")} disabled={isLoading || userHardCount >= 1} size="sm" title={userHardCount >= 1 ? "Du hast bereits einen Platz fest reserviert." : undefined}>
                            {isLoading ? "…" : "Fest reservieren"}
                          </Button>
                        )}
                        {!selectedSeat.is_reserved && isRegistered && (
                          <Button
                            onClick={() => handleReserveSeat(selectedSeat, "soft")}
                            disabled={isLoading || userSoftCount >= softReserveLimit}
                            variant="outline"
                            size="sm"
                            title={userSoftCount >= softReserveLimit ? `Maximal ${softReserveLimit} Vormerke erlaubt.` : undefined}
                          >
                            {isLoading ? "…" : `Vormerken (${userSoftCount}/${softReserveLimit})`}
                          </Button>
                        )}
                        {!hasPaid && !(!selectedSeat.is_reserved && isRegistered) && (
                          <p className="text-sm text-muted-foreground">{isRegistered ? "Bezahle die Teilnahmegebühr, um einen Platz fest zu reservieren." : "Melde dich für das Event an, um Plätze vorzumerken oder zu reservieren."}</p>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
